import { PluginObject } from 'vue';
import router from '@/router';

import * as Sentry from '@sentry/vue';
import { Breadcrumb, BreadcrumbHint } from '@sentry/vue';
import { Integrations } from '@sentry/tracing';

export interface VueSentryOptions {
  enabled: boolean;
  dsn: string;
  tracing: {
    enabled: boolean;
    sampleRate: number;
  };
  logErrors: boolean;
  breadcrumbs: {
    hook: {
      enabled: boolean;
    };
  };
}

const SentryPlugin: PluginObject<VueSentryOptions> = {
  install(Vue, options): void {
    if (options.enabled) {
      const integrations = [];
      if (options.tracing.enabled) {
        integrations.push(
          new Integrations.BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
            tracingOrigins: ['*'],
          }),
        );
      }
      const extraOptions = {};
      if (options.breadcrumbs.hook.enabled) {
        extraOptions['beforeBreadcrumb'] = function (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb {
          if (breadcrumb.category.startsWith('ui')) {
            let element = hint.event.target;
            let tag;
            // Find first element with sentry-tag or id
            while (element && !tag) {
              tag = element.attributes['sentry-tag']?.value ?? element.id;
              if (!tag) {
                element = element.parentElement;
              }
            }
            if (tag) {
              breadcrumb.message = `[${tag.toString()}] ${breadcrumb.message}`;
            }
          }
          return breadcrumb;
        };
      }

      Sentry.init({
        Vue: Vue,
        dsn: options.dsn,
        // Tunnel envelopes through the backend so adblockers (which match
        // `sentry.*` hostnames and `/api/N/envelope/` paths) don't drop them.
        // The backend validates the envelope DSN against the configured
        // upstream and forwards the body verbatim.
        tunnel: '/api/sentry-tunnel',
        // Release identifier baked in at build time by CI (vue-cli inlines
        // VUE_APP_* env vars during webpack). Matches the deployed image tag
        // (e.g. master.2771.abdefd84), which is also what the release_frontend
        // CI job uploads source maps under, so GlitchTip can resolve minified
        // frames back to Vue source.
        release: process.env.VUE_APP_SENTRY_RELEASE,
        integrations,
        tracesSampleRate: options.tracing.sampleRate,
        logErrors: options.logErrors,
        ...extraOptions,
      });
    }

    Vue.prototype.$sentry = Sentry;
  },
};

export default SentryPlugin;
